import React from 'react'
import { PartnerService } from '@duck/api-definitions'
import { Textarea, MultiPick, SinglePick } from '@duck/ui'
import { TranslationKey } from '@/modules/i18n/types'
import { useTar } from '@/modules'
import { useTasks } from '@/hooks/useTasks'

interface OrganizationFormProps {
    formData: Partial<PartnerService.Organization>
    onChange: (field: keyof PartnerService.Organization, value: string | boolean | PartnerService.OrganizationType | string[]) => void
    onSave: () => void
    onCancel: () => void
    isSaving?: boolean
    saveError?: string | null
    isEditing?: boolean
    isReadOnly?: boolean
}

const typeOptions: { value: PartnerService.OrganizationType, label: TranslationKey }[] = [
    { value: 'company', label: 'common.company' },
    { value: 'school', label: 'common.school' },
]

export const OrganizationForm: React.FC<OrganizationFormProps> = ({
    formData,
    onChange,
    onSave,
    onCancel,
    isSaving = false,
    saveError,
    isEditing = false,
    isReadOnly = false,
}) => {
    const { t } = useTar()
    const { tasks, isLoading: isLoadingTasks } = useTasks()

    const renderFormField = (
        label: TranslationKey,
        field: keyof PartnerService.Organization,
        type: 'text' | 'select' | 'checkbox' | 'textarea' = 'text',
        required = false,
        options?: readonly { value: string; label: TranslationKey }[],
        disabled = false
    ) => {
        const value = formData[field]
        const baseInputClasses = `
      w-full px-3 py-2 text-sm border border-border rounded-md 
      bg-card text-foreground placeholder-secondary/60
      focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
      transition-colors
      ${disabled ? 'bg-secondary/5 text-muted-foreground cursor-not-allowed' : 'hover:border-secondary'}
    `

        // Render read-only text when in read-only mode
        if (isReadOnly) {
            return (
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-muted-foreground">
                        {t(label)}
                    </label>
                    {type === 'checkbox' ? (
                        <div className="text-foreground">
                            {value ? t('common.yes') : t('common.no')}
                        </div>
                    ) : type === 'select' && options ? (
                        <div className="text-foreground">
                            {t(options.find(opt => opt.value === value)?.label ?? 'common.na')}
                        </div>
                    ) : (
                        <div className="text-foreground">
                            {value?.toString() ?? t('common.na')}
                        </div>
                    )}
                </div>
            )
        }

        return (
            <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                    {t(label)}
                    {required && <span className="text-error ml-1">*</span>}
                </label>
                {type === 'checkbox' ? (
                    <label className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            autoComplete="off"
                            checked={value as boolean}
                            onChange={(e) => onChange(field, e.target.checked)}
                            disabled={disabled}
                            className="h-4 w-4 text-accent focus:ring-accent border-border rounded"
                        />
                        <span className="text-sm text-foreground">{t('common.active')}</span>
                    </label>
                ) : type === 'select' && options ? (
                    <SinglePick
                        options={options.map(opt => ({ label: t(opt.label), value: opt.value as string }))}
                        selected={(value as string | undefined) ?? undefined}
                        onChange={(selected) => onChange(field, selected ?? '')}
                        placeholder={t('common.select', { value: t(label).toLowerCase() })}
                    />
                ) : type === 'textarea' ? (
                    <Textarea
                        value={value as string || ''}
                        onChange={(e) => onChange(field, e.target.value)}
                        disabled={disabled || isSaving}
                        placeholder={t('common.enterValue', { value: t(label).toLowerCase() })}
                        className="min-h-[100px] resize-y"
                        autoComplete="off"
                    />
                ) : (
                    <input
                        type="text"
                        autoComplete="off"
                        value={value as string || ''}
                        onChange={(e) => onChange(field, e.target.value)}
                        disabled={disabled || isSaving}
                        className={baseInputClasses}
                        placeholder={t('common.enterValue', { value: t(label).toLowerCase() })}
                    />
                )}
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
                    {t('common.basicInformation')}
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="lg:col-span-2">
                        {renderFormField('common.name', 'name', 'text', true)}
                    </div>
                    <div className="lg:col-span-2">
                        {renderFormField('common.description', 'description', 'textarea', false)}
                    </div>
                    <div className="lg:col-span-2">
                        {renderFormField('common.type', 'type', 'select', true, typeOptions)}
                    </div>
                    {/* <div className="lg:col-span-1">
                        {renderFormField('common.active', 'active', 'checkbox', true)}
                    </div>
                    <div className="lg:col-span-2">
                        {renderFormField('common.logoURL', 'logoURL', 'text', false)}
                    </div> */}
                    <div className="lg:col-span-2">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-foreground">
                                {isReadOnly ? (
                                    <span className="text-muted-foreground">{t('organizations.availableTasks' as TranslationKey)}</span>
                                ) : (
                                    <>
                                        {t('organizations.availableTasks' as TranslationKey)}
                                    </>
                                )}
                            </label>
                            {isReadOnly ? (
                                <div className="text-foreground">
                                    {formData.availableTaskIds && formData.availableTaskIds.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {formData.availableTaskIds.map((taskId) => {
                                                const task = tasks.find(t => t.id === taskId)
                                                return (
                                                    <span key={taskId} className="px-2 py-1 bg-secondary/10 rounded text-sm">
                                                        {task?.name ?? taskId}
                                                    </span>
                                                )
                                            })}
                                        </div>
                                    ) : (
                                        t('common.na')
                                    )}
                                </div>
                            ) : (
                                <>
                                    <MultiPick
                                        options={tasks.map(task => ({
                                            label: task.name,
                                            value: task.id,
                                        }))}
                                        selected={formData.availableTaskIds ?? []}
                                        onChange={(selected) => onChange('availableTaskIds', selected)}
                                        placeholder={`${t('common.select')} ${t('tasks.task').toLowerCase()}s`}
                                        emptyMessage={t('tasks.noTasksMatchFilters')}
                                        disabled={isSaving || isLoadingTasks}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {saveError && (
                <div className="p-3 bg-error/10 border border-error/20 rounded-md">
                    <p className="text-sm text-error">{saveError}</p>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-border justify-end">
                <button
                    onClick={onCancel}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-md hover:bg-secondary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isReadOnly ? t('common.close') : t('common.cancel')}
                </button>
                {!isReadOnly && (
                    <button
                        onClick={onSave}
                        disabled={isSaving}
                        className="px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {isSaving && (
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                        )}
                        {isSaving ? t('common.saving') : isEditing ? t('organizations.updateOrganization') : t('organizations.createOrganization')}
                    </button>
                )}
            </div>
        </div>
    )
}

